<template>
  <NolaNovelListItem :novel="novel">
    <template #container>
      <div class="title">{{ title }}</div>
      <div class="data">
        <div>{{ $t('nolaNovelNonAssociatedNovelListItem.lastUpdateDate') }}{{ updatedAt }}</div>
        <div>{{ $t('nolaNovelNonAssociatedNovelListItem.createdDate') }}{{ createdAt }}</div>
      </div>
    </template>
    <template #right>
      <button class="button primary" @click="onClickPublish">
        <div>{{ $t('nolaNovelNonAssociatedNovelListItem.publishToReadersButton') }}</div>
        <OpenInNewIcon class="icon" :size="16" />
      </button>
      <button class="button tertiary" @click="onClickTransNovel">
        <div>{{ $t('nolaNovelNonAssociatedNovelListItem.navigateToNovelPageButton') }}</div>
        <ChevronRightIcon class="icon" :size="16" />
      </button>
    </template>
  </NolaNovelListItem>
</template>

<script lang="ts">
import Vue, { PropType } from "vue";
import { format } from "date-fns";
import ChevronRightIcon from "icons/ChevronRight.vue";
import OpenInNewIcon from "icons/OpenInNew.vue";
import NolaNovelListItem from "@/components/molecules/NolaNovelListItem.vue";
import NolaNovelIntroduction from "@/components/ui/nolaNovel/NolaNovelIntroduction.vue";
import { ConnectedOtherServices, Novel } from "@/lib/models";
import { Dialog } from "@/lib/utils";
import { NolaNovelUrlGenerator } from "@/lib/utils/generator/nolanovelUrl";
import { createUrlWithUtmParams } from "@/lib/utils/url";
import { NolaItemId, NolaPageName } from "@/lib/utils/analytics";

export default Vue.extend<Data, Methods, Computed, Props>({
  components: { ChevronRightIcon, OpenInNewIcon, NolaNovelListItem },
  props: {
    novel: Object as PropType<Novel>,
  },
  computed: {
    title() {
      return this.novel.title;
    },
    updatedAt() {
      const { updatedAtLatestDataInNovel, updatedAt } = this.novel;
      return format(updatedAtLatestDataInNovel || updatedAt!, "YYYY/MM/DD");
    },
    createdAt() {
      const { createdAt } = this.novel;
      return format(createdAt!, "YYYY/MM/DD");
    },
    isConnectedNolaNovel() {
      const connectedOtherServices = this.$store.getters["userModule/connectedOtherServices"] as ConnectedOtherServices;
      return connectedOtherServices.nolaNovel.result;
    },
  },
  methods: {
    async onClickPublish() {
      if (!this.isConnectedNolaNovel) {
        const introduction = new Dialog(NolaNovelIntroduction);
        introduction.show();
        return;
      }

      const url = process.env.VUE_APP_NOLANOVEL_WEB;

      if (!url) {
        throw new Error(this.$t('nolaNovelNonAssociatedNovelListItem.errors.nolaNovelUrlNotFound'));
      }

      const { novel } = this;

      const service = new NolaNovelUrlGenerator(process.env.VUE_APP_NOLANOVEL_WEB!);
      const postNovelUrl = await service.genPostNovelUrl(novel.novelId);
      window.open(
        createUrlWithUtmParams(postNovelUrl, NolaPageName.NolaNovel, NolaItemId.PublishToReadersButton),
        process.env.VUE_APP_NOLANOVEL_NAME
      );
    },
    onClickTransNovel() {
      this.$router.push({ name: "editor", params: { novelId: this.novel.novelId } });
    },
  },
});

interface Props {
  novel: Novel;
}

interface Data {}

interface Computed {
  title: string;
  updatedAt: string;
  createdAt: string;
  isConnectedNolaNovel?: boolean;
}

interface Methods {
  onClickPublish(): void;
  onClickTransNovel(): void;
}
</script>

<style lang="scss" scoped>
.data {
  grid-template-columns: repeat(1, 1fr);
  gap: 5px 10px;
}

.button {
  margin: 0;
  margin-bottom: -30px;

  &:last-of-type {
    margin-bottom: 0;
  }
}
</style>
